import { useState, useEffect } from "react";
import { Box, Button, IconButton } from "@mui/material";
import { Formik, FieldArray } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import axios from "axios";
import { Add, Delete } from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import { 
  TextFieldCustom,
  SelectField,
  DateField
} from "./CustomInputs";

const SalesForm = ({ initialValues, handleFormSubmit, buttonLabel }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [employee, setEmployee] = useState([]);
  const apiUrl = "https://oven-backend.onrender.com";

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const response = await axios.get(`${apiUrl}/employees/role/customer`);
        setEmployee(response.data);
      } catch (error) {
        console.error(t('error_fetching_customers'), error);
      }
    };

    fetchEmployee();
  }, [t]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={salesSchema(t)}
      onSubmit={handleFormSubmit}
      enableReinitialize={true}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit}>
          <Box
            display="grid"
            gap="30px"
            gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            sx={{
              "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              direction: isRTL ? 'rtl' : 'ltr'
            }}
          >
            <SelectField
              name="employee_id"
              label={t('customer')}
              formik={formik}
              options={employee.map(emp => ({
                value: emp._id,
                labelKey: emp.name
              }))}
              grid={2}
            />

            <DateField
              name="date"
              label={t('sale_date')}
              formik={formik}
              grid={2}
            />

            <SelectField
              name="payment_status"
              label={t('payment_status')}
              formik={formik}
              options={[
                { value: 'pending', labelKey: t('payment_status_pending') },
                { value: 'paid', labelKey: t('payment_status_paid') }
              ]}
              grid={2}
            />

            <SelectField
              name="inventory_status"
              label={t('inventory_status')}
              formik={formik}
              options={[
                { value: 'pending', labelKey: t('inventory_status_pending') },
                { value: 'cleared', labelKey: t('inventory_status_cleared') }
              ]}
              grid={2}
            />
          </Box>

          <Box
            display="grid"
            gap="30px"
            marginTop="30px"
            sx={{
              direction: isRTL ? 'rtl' : 'ltr',
              "& > div": { gridColumn: isNonMobile ? undefined : "span 2" },
            }}
          >
        <FieldArray name="products">
  {({ push, remove }) => {
    return (
      <Box display="flex" flexDirection="column" width="100%">
        {formik.values.products && formik.values.products.length > 0 ? (
          formik.values.products.map((product, index) => (
            <Box
              key={index}
              display="flex"
              flexDirection={isNonMobile ? "row" : "column"}
              gap="10px"
              alignItems="center"
              marginTop="20px"
            >
              <TextFieldCustom
                name={`products[${index}].name_sales`}
                label={t("product_name")}
                formik={formik}
                sx={{ flex: 3, width: isNonMobile ? "auto" : "100%" }}
              />
              <TextFieldCustom
                name={`products[${index}].price`}
                label={t("price")}
                formik={formik}
                type="number"
                sx={{ flex: 2, width: isNonMobile ? "auto" : "100%" }}
              />
              <TextFieldCustom
                name={`products[${index}].quantity`}
                label={t("quantity")}
                formik={formik}
                type="number"
                sx={{ flex: 2, width: isNonMobile ? "auto" : "100%" }}
              />
              <IconButton
                onClick={() => remove(index)}
                color="error"
                sx={{
                  marginLeft: isRTL ? 0 : 1,
                  marginRight: isRTL ? 1 : 0,
                  alignSelf: isNonMobile ? "center" : "start",
                }}
              >
                <Delete sx={{ transform: isRTL ? "scaleX(-1)" : "none" }} />
              </IconButton>
            </Box>
          ))
        ) : (
          <Box mt={2}>{t("no_products")}</Box>
        )}
        <Button
          type="button"
          color="secondary"
          onClick={() => push({ name_sales: "", quantity: "", price: "" })}
          startIcon={<Add sx={{ transform: isRTL ? "" : "none" }} />}
          sx={{
            mt: 2,
            alignSelf: isRTL ? "flex-start" : "flex-start",
            fontFamily: isRTL ? "Cairo, sans-serif" : "Roboto, sans-serif",
          }}
        >
          {t("add_product")}
        </Button>
      </Box>
    );
  }}
</FieldArray>


          </Box>

          <Box display="flex" justifyContent="end" mt="20px">
            <Button 
              type="submit" 
              color="secondary" 
              variant="contained"
              sx={{ 
                fontFamily: isRTL ? 'Cairo, sans-serif' : 'Roboto, sans-serif',
                minWidth: '120px'
              }}
            >
              {buttonLabel}
            </Button>
          </Box>
        </form>
      )}
    </Formik>
  );
};

const salesSchema = (t) => yup.object().shape({
  employee_id: yup.string().required(t('required_employee')),
  products: yup.array().of(
    yup.object().shape({
      name_sales: yup.string().required(t('required_product_name')),
      quantity: yup.number()
        .positive(t('positive_number'))
        .integer(t('integer_number'))
        .required(t('required_quantity')),
      price: yup.number()
        .positive(t('positive_number'))
        .required(t('required_price')),
    })
  ),
  payment_status: yup.string()
    .oneOf(["pending", "paid"], t('invalid_payment_status'))
    .required(t('required_payment_status')),
  inventory_status: yup.string()
    .oneOf(["cleared", "pending"], t('invalid_inventory_status'))
    .required(t('required_inventory_status')),
  date: yup.string().required(t('required_sale_date')),
});

export default SalesForm;